import React from 'react';

// One-time reload guard to recover from stale/missing chunk assets after deploys.
const CHUNK_LOAD_RETRY_KEY = 'psylaris:chunk-load-retry';
// Match common dynamic-import/chunk load failure messages across browsers.
const CHUNK_LOAD_ERROR_PATTERNS = [
  /Loading chunk \d+ failed/i,
  /ChunkLoadError/i,
  /Importing a module script failed/i,
  /Failed to fetch dynamically imported module/i,
];

// SessionStorage access can throw in some browser modes; fail closed.
const getSessionStorage = () => {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch (error) {
    return null;
  }
};

// Only trigger recovery for known chunk/dynamic import failures.
const shouldReloadForChunkError = (error) => {
  const message = error?.message || '';
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

// Retry a failed dynamic import once before falling back to reload logic.
const importWithSingleRetry = (importer) =>
  importer().catch((error) => {
    if (shouldReloadForChunkError(error)) {
      console.info('[chunk-load] retrying dynamic import once');
      return importer();
    }
    throw error;
  });

// Wrap React.lazy so a chunk-load failure triggers a single reload.
export const lazyImportWithRetry = (importer) =>
  React.lazy(() =>
    importWithSingleRetry(importer)
      .then((module) => {
        const storage = getSessionStorage();
        if (storage) {
          storage.removeItem(CHUNK_LOAD_RETRY_KEY);
        }
        return module;
      })
      .catch((error) => {
        if (shouldReloadForChunkError(error)) {
          const storage = getSessionStorage();
          if (storage && !storage.getItem(CHUNK_LOAD_RETRY_KEY)) {
            storage.setItem(CHUNK_LOAD_RETRY_KEY, '1');
            console.info('[chunk-load] forcing one-time reload after import failure');
            // Reload once to pick up the latest HTML + assets.
            window.location.reload();
          }
        }
        throw error;
      }),
  );
